import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useNotify } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { StatusBadge } from "../data-display/StatusBadge";
import { EmptyState } from "../data-display/EmptyState";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import {
  CUSTOM_FIELD_TYPES,
  labelToKey,
  useCustomFieldDefinitions,
  type CustomFieldEntity,
  type CustomFieldType,
} from "./useCustomFields";

const ENTITIES: { value: CustomFieldEntity; label: string }[] = [
  { value: "contacts", label: "Contacts" },
  { value: "companies", label: "Companies" },
  { value: "deals", label: "Deals" },
];

/**
 * Settings panel for workspace custom fields: define text/number/date/
 * yes-no/select fields for contacts, companies and deals, no developer
 * required. Definitions are workspace-scoped; RLS gates writes to
 * settings.manage.
 */
export const CustomFieldsManager = () => {
  const [entity, setEntity] = useState<CustomFieldEntity>("contacts");
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data: definitions = [] } = useCustomFieldDefinitions(entity);
  const notify = useNotify();
  const queryClient = useQueryClient();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["gravun", "custom-fields"] });

  const removeField = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await getSupabaseClient()
        .from("custom_field_definitions")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      notify("Custom field removed", { type: "info" });
      invalidate();
    },
    onError: () => notify("Could not remove the field", { type: "error" }),
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <Tabs
          value={entity}
          onValueChange={(value) => setEntity(value as CustomFieldEntity)}
        >
          <TabsList className="h-8">
            {ENTITIES.map((option) => (
              <TabsTrigger
                key={option.value}
                value={option.value}
                className="px-2.5 text-[12.5px]"
              >
                {option.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 rounded-md px-2.5 text-[12.5px] shadow-none"
          onClick={() => setDialogOpen(true)}
        >
          <Plus className="size-3.5" />
          Add field
        </Button>
      </div>

      {definitions.length === 0 ? (
        <EmptyState
          title="No custom fields yet"
          description={`Add a field and it appears on every ${entity.slice(0, -1)} form in this workspace.`}
        />
      ) : (
        <div className="flex flex-col divide-y divide-border-subtle rounded-lg border border-border-subtle">
          {definitions.map((definition) => (
            <div
              key={definition.id}
              className="flex items-center justify-between gap-3 px-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="truncate text-[13px] font-medium text-foreground">
                  {definition.label}
                </span>
                <StatusBadge
                  label={
                    CUSTOM_FIELD_TYPES.find(
                      (type) => type.value === definition.field_type,
                    )?.label ?? definition.field_type
                  }
                  tone="neutral"
                />
                {definition.field_type === "select" &&
                definition.options.length > 0 ? (
                  <span className="truncate text-[11.5px] text-muted-foreground">
                    {definition.options.join(" · ")}
                  </span>
                ) : null}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 text-muted-foreground hover:text-destructive"
                aria-label={`Remove ${definition.label}`}
                onClick={() => removeField.mutate(definition.id)}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <AddFieldDialog
        entity={entity}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onAdded={invalidate}
        existingCount={definitions.length}
      />
    </div>
  );
};

const AddFieldDialog = ({
  entity,
  open,
  onOpenChange,
  onAdded,
  existingCount,
}: {
  entity: CustomFieldEntity;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: () => void;
  existingCount: number;
}) => {
  const notify = useNotify();
  const [label, setLabel] = useState("");
  const [fieldType, setFieldType] = useState<CustomFieldType>("text");
  const [optionsText, setOptionsText] = useState("");

  const addField = useMutation({
    mutationFn: async () => {
      const trimmedLabel = label.trim();
      if (!trimmedLabel) throw new Error("Enter a field label");
      const key = labelToKey(trimmedLabel);
      if (!key) throw new Error("Label must contain letters or numbers");
      const options =
        fieldType === "select"
          ? optionsText
              .split(",")
              .map((option) => option.trim())
              .filter(Boolean)
          : [];
      if (fieldType === "select" && options.length === 0) {
        throw new Error("Add at least one option (comma separated)");
      }
      const { error } = await getSupabaseClient()
        .from("custom_field_definitions")
        .insert({
          workspace_id: getActiveWorkspaceId(),
          entity,
          key,
          label: trimmedLabel,
          field_type: fieldType,
          options,
          position: existingCount,
        });
      if (error) {
        throw new Error(
          error.code === "23505"
            ? "A field with this name already exists"
            : error.message,
        );
      }
    },
    onSuccess: () => {
      notify("Custom field added", { type: "success" });
      onOpenChange(false);
      setLabel("");
      setFieldType("text");
      setOptionsText("");
      onAdded();
    },
    onError: (error: Error) =>
      notify(error.message || "Could not add the field", { type: "error" }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add a custom field</DialogTitle>
          <DialogDescription>
            The field appears on every {entity.slice(0, -1)} form in this
            workspace.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-1">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cf-label">Label</Label>
            <Input
              id="cf-label"
              placeholder="e.g. Furnace age"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cf-type">Type</Label>
            <Select
              value={fieldType}
              onValueChange={(value) => setFieldType(value as CustomFieldType)}
            >
              <SelectTrigger id="cf-type" className="rounded-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CUSTOM_FIELD_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {fieldType === "select" ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cf-options">Options (comma separated)</Label>
              <Input
                id="cf-options"
                placeholder="Gas, Electric, Heat pump"
                value={optionsText}
                onChange={(event) => setOptionsText(event.target.value)}
              />
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => addField.mutate()}
            disabled={addField.isPending}
          >
            {addField.isPending ? "Adding..." : "Add field"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
